import React from 'react';
import { Wallet, TrendingUp, Target, Flame, Zap, Trophy, Bell, Search, DollarSign } from 'lucide-react';

interface DashboardPreviewProps {
  isDark?: boolean;
  className?: string;
}

export function DashboardPreview({ isDark = false, className = '' }: DashboardPreviewProps) {
  const bgColor = isDark ? 'bg-[#0A0A0B]' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-slate-900';
  const mutedColor = isDark ? 'text-slate-400' : 'text-slate-500';
  const cardBg = isDark ? 'bg-white/5' : 'bg-slate-50';
  const borderColor = isDark ? 'border-white/10' : 'border-slate-200';

  return (
    <div className={`w-full h-full ${bgColor} ${textColor} ${className} overflow-hidden flex flex-col`}>
      {/* Top Navigation Bar */}
      <div className={`border-b ${borderColor} px-6 py-4 flex items-center justify-between bg-white/5 backdrop-blur-md`}>
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-lg tracking-tight">KwachaLite</span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="relative hidden xl:block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search..." 
              className={`pl-10 pr-4 py-2 rounded-xl border ${borderColor} ${isDark ? 'bg-white/5 text-white' : 'bg-white'} text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
              readOnly
            />
          </div>
          
          <button className="relative p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
          </button>
          
          <div className="flex items-center gap-3 pl-2 border-l border-white/10">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full border border-white/20"></div>
            <div className="hidden lg:block text-left">
              <p className="text-xs font-bold leading-none mb-1">John Doe</p>
              <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">PREMIUM</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8 space-y-8 flex-1 overflow-y-auto custom-scrollbar">
        {/* Welcome Header */}
        <div className="flex items-end justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className={mutedColor}>Welcome back! You've saved <span className="text-green-500 font-semibold">12% more</span> than last month.</p>
          </div>
          <div className="flex gap-3">
            <button className="px-5 py-2.5 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors text-sm font-bold">
              Reports
            </button>
            <button className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-500 transition-colors text-sm font-bold flex items-center gap-2 shadow-lg shadow-blue-600/20">
              <Zap className="w-4 h-4 fill-current" />
              Quick Action
            </button>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-4 gap-6">
          {[
            { label: 'Net Balance', value: 'K 245,890', trend: '+12.5%', color: 'blue', icon: <Wallet /> },
            { label: 'Income', value: 'K 485,000', trend: '+8.2%', color: 'green', icon: <TrendingUp /> },
            { label: 'Expenses', value: 'K 239,110', trend: '-3.1%', color: 'red', icon: <TrendingUp className="rotate-180" /> },
            { label: 'Savings Rate', value: '24.7%', trend: 'On track', color: 'purple', icon: <Target /> },
          ].map((card, i) => (
            <div key={i} className={`${cardBg} border ${borderColor} rounded-2xl p-6 hover:border-blue-500/30 transition-all group`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2.5 rounded-xl bg-${card.color}-500/10 text-${card.color}-500 group-hover:scale-110 transition-transform`}>
                  {React.cloneElement(card.icon as React.ReactElement, { className: 'w-5 h-5' })}
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-lg bg-${card.color === 'red' ? 'red' : 'green'}-500/10 text-${card.color === 'red' ? 'red' : 'green'}-500`}>
                  {card.trend}
                </span>
              </div>
              <p className={`text-xs font-bold uppercase tracking-wider ${mutedColor} mb-1`}>{card.label}</p>
              <p className="text-2xl font-bold tracking-tight">{card.value}</p>
            </div>
          ))}
        </div>

        {/* Charts and Recent Transactions */}
        <div className="grid grid-cols-12 gap-6">
          {/* Recent Transactions */}
          <div className={`col-span-8 ${cardBg} border ${borderColor} rounded-2xl p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold tracking-tight">Recent Activity</h3>
              <button className="text-xs font-bold text-blue-500 uppercase tracking-widest hover:underline">View All</button>
            </div>
            <div className="space-y-4">
              {[
                { name: 'Shoprite', category: 'Groceries', amount: -45800, time: '2 hours ago', icon: '🛒' },
                { name: 'Salary Payment', category: 'Income', amount: 485000, time: 'Yesterday', icon: '💰' },
                { name: 'Uber Technologies', category: 'Transport', amount: -12500, time: 'Yesterday', icon: '🚗' },
                { name: 'Electric Bill', category: 'Utilities', amount: -38900, time: '2 days ago', icon: '⚡' },
              ].map((tx, index) => (
                <div key={index} className={`flex items-center justify-between p-4 rounded-xl border border-transparent hover:border-white/5 hover:bg-white/5 transition-all group cursor-default`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                      {tx.icon}
                    </div>
                    <div>
                      <p className="font-bold text-sm mb-0.5">{tx.name}</p>
                      <p className={`text-xs ${mutedColor}`}>{tx.category} • {tx.time}</p>
                    </div>
                  </div>
                  <span className={`font-bold text-sm ${tx.amount > 0 ? 'text-green-500' : textColor}`}>
                    {tx.amount > 0 ? '+' : ''}K {Math.abs(tx.amount).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Goals and Streaks */}
          <div className="col-span-4 space-y-6">
            <div className={`${cardBg} border ${borderColor} rounded-2xl p-6`}>
              <h3 className="text-lg font-bold tracking-tight mb-4">Goal Progress</h3>
              <div className="space-y-5">
                {[
                  { name: 'Emergency Fund', progress: 75, color: 'bg-blue-500' },
                  { name: 'Vacation', progress: 45, color: 'bg-indigo-500' },
                  { name: 'New Laptop', progress: 20, color: 'bg-purple-500' },
                ].map((goal, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between text-xs font-bold">
                      <span className={mutedColor}>{goal.name}</span>
                      <span>{goal.progress}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                      <div className={`${goal.color} h-full rounded-full`} style={{ width: `${goal.progress}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-2xl p-6 relative overflow-hidden`}>
              <div className="absolute -right-4 -bottom-4 opacity-10 scale-150 rotate-12">
                <Flame className="w-24 h-24 text-orange-500" />
              </div>
              <div className="relative z-10 flex items-center gap-4">
                <div className="p-3 bg-orange-500 rounded-xl shadow-lg shadow-orange-500/20">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-0.5">Active Streak</p>
                  <p className="text-xl font-bold">15 Days Saving! 🔥</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}